import React, {memo} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChannelModel} from '../../../../../libs/shared/core/src/models/api/channelModel';
import {DraftMessageModel} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {getHasNewSynchronousP3} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {
  ComposerPanelId,
  ViewerPanelId,
} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ToolbarButton} from '../../../../../libs/shared/ui/src/components/buttons/toolbarButton';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {PermissionCheck} from '../../../../../srcCommon/permissions/permissionCheck';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {ApplicationComposerToolbarEntryPointModel} from '../../../../models/api/applicationEntryPointModel';
import {ConversationModel} from '../../../../models/api/conversationModel';
import {PermissionsEnum} from '../../../../models/api/roleModel';
import {FlexSpacer} from '../../../common/flex';
import {BaseComposerToolbar} from './baseComposerToolbar';
import {AnswersToolbarButtonProps} from './buttons/answersToolbarButton';
import {AnswersToolbarButtonContainer} from './buttons/answersToolbarButtonContainer';
import {AttachmentsToolbarButtonPublicProps} from './buttons/attachmentsToolbarButton';
import {AttachmentsToolbarButtonContainer} from './buttons/attachmentsToolbarButtonContainer';
import {DeleteToolbarButton, DeleteToolbarButtonProps} from './buttons/deleteToolbarButton';
import {DoneToolbarButton, DoneToolbarButtonProps} from './buttons/doneToolbarButton';
import {DraftAssistantToolbarButton} from './buttons/draftAssistantToolbarButton';
import {EmojiToolbarButton, EmojiToolbarButtonProps} from './buttons/emojiToolbarButton';
import {GifToolbarButton, GifToolbarButtonProps} from './buttons/gifToolbarButton';
import {
  KnowledgeBaseToolbarButton,
  KnowledgeBaseToolbarButtonProps,
} from './buttons/knowledgeBaseToolbarButton';
import {SchedulingToolbarButton, SchedulingToolbarButtonProps} from './buttons/schedulingToolbarButton';
import {
  SendToolbarButtonContainer,
  SendToolbarButtonContainerProps,
} from './buttons/sendToolbarButtonContainer';
import {TagsToolbarButton, TagsToolbarButtonProps} from './buttons/tagsToolbarButton';
import {DraftSaveIndicator, DraftSaveStatusesEnum} from './draftSaveIndicator';
import {ApplicationComposerEntrypointButtons} from './entryPoints/applicationComposerEntryPointButtons';

/*
 * Props.
 */

export interface FullComposerToolbarProps
  extends EmojiToolbarButtonProps,
    GifToolbarButtonProps,
    AttachmentsToolbarButtonPublicProps,
    Omit<AnswersToolbarButtonProps, 'channelId'>,
    DeleteToolbarButtonProps,
    Omit<SendToolbarButtonContainerProps, 'hasArchivePermission'>,
    DoneToolbarButtonProps,
    SchedulingToolbarButtonProps,
    TagsToolbarButtonProps,
    KnowledgeBaseToolbarButtonProps {
  channel: ChannelModel;
  message: DraftMessageModel;
  panelId: ComposerPanelId | ViewerPanelId;
  isUserVisitor?: boolean;
  supportsFormatting?: boolean;
  supportsGifs?: boolean;
  supportsScheduling?: boolean;
  isFormattingToolbarOpen?: boolean;
  toggleIsFormattingToolbarOpen?: () => void;
  conversation: ConversationModel | null;
  draftSaveStatus: DraftSaveStatusesEnum;
  isDraftSaving: boolean;
  composerEntryPoints?: ReadonlyArray<ApplicationComposerToolbarEntryPointModel>;
  hasAICompose?: boolean;
  isAIDisabled?: boolean;
  onDraftAssistantInsertDraft?: (arg: string) => void;
  shouldHideAttachmentsButton?: boolean;
  hasSaveErrorMessage?: boolean;
  isMarkdownEditor: boolean;
}

/*
 * Component.
 */

export const FullComposerToolbar = memo<FullComposerToolbarProps>((props) => {
  const hasNewSynchronousP3 = useAppSelector(getHasNewSynchronousP3);
  return (
    <UserThemeConsumer>
      {(theme) => (
        <BaseComposerToolbar hasSynchronousTimeline={false}>
          {props.supportsFormatting && (
            <ToolbarButton
              iconName="formatting"
              onClick={props.toggleIsFormattingToolbarOpen}
              label={<Trans id="composer-formatting">Formatting options</Trans>}
              color={theme.greys.shade80}
              hoverColor={theme.greys.shade80}
              hasHover={props.isFormattingToolbarOpen}
            />
          )}
          <EmojiToolbarButton {...props} />
          {maybeRenderGifsButton(props)}
          {!props.shouldHideAttachmentsButton && <AttachmentsToolbarButtonContainer {...props} />}
          {!props.isUserVisitor && <AnswersToolbarButtonContainer {...props} channelId={props.channel.id} />}
          {maybeRenderKnowledgeBaseButton(props)}
          {maybeRenderSchedulingButton(props)}
          {maybeRenderTagsButton(props)}
          {maybeRenderDraftAssistantButton(props)}
          {props.composerEntryPoints && (
            <ApplicationComposerEntrypointButtons
              panelId={props.panelId}
              entryPoints={props.composerEntryPoints}
              draftMessageUid={props.message.uid}
              alwaysUseOverflowButton={hasNewSynchronousP3}
            />
          )}
          {!props.isUserVisitor && <DeleteToolbarButton {...props} />}
          <FlexSpacer />
          <DraftSaveIndicator
            status={props.draftSaveStatus}
            isSaving={props.isDraftSaving}
            // Checks if the banner is rendered(the saveError is populated), then don't render the tooltip for the status
            errorMessage={!props.hasSaveErrorMessage ? props.message.error?.description : undefined}
          />
          {renderRightActions(props)}
        </BaseComposerToolbar>
      )}
    </UserThemeConsumer>
  );
});

function maybeRenderTagsButton(props: FullComposerToolbarProps) {
  const {message} = props;

  // If we're replying to an existing message, the tag button is redundant with the topbar.
  if (message.inReplyToId) {
    return null;
  }

  return <TagsToolbarButton {...props} />;
}

function maybeRenderSchedulingButton(props: FullComposerToolbarProps) {
  if (!props.supportsScheduling) {
    return null;
  }

  if (props.isMarkdownEditor) {
    return null;
  }

  return <SchedulingToolbarButton {...props} />;
}

function maybeRenderGifsButton(props: FullComposerToolbarProps) {
  if (!props.supportsGifs) {
    return null;
  }

  if (props.isMarkdownEditor) {
    return null;
  }

  return <GifToolbarButton {...props} />;
}

function maybeRenderDraftAssistantButton(props: FullComposerToolbarProps) {
  if (!props.hasAICompose || !props.onDraftAssistantInsertDraft) {
    return null;
  }

  return (
    <DraftAssistantToolbarButton
      panelId={props.panelId}
      onDraftAssistantInsertDraft={props.onDraftAssistantInsertDraft}
      conversation={props.conversation}
      message={props.message}
      isDisabled={Boolean(props.isAIDisabled)}
    />
  );
}

function renderRightActions(props: FullComposerToolbarProps) {
  if (props.isUserVisitor) {
    return <DoneToolbarButton {...props} />;
  }

  return (
    <PermissionCheck namespaces={[props.namespace]} permission={PermissionsEnum.CONVERSATION_ARCHIVE}>
      {(archivePermissionResult) => (
        <SendToolbarButtonContainer
          cellsContext={props.cellsContext}
          conversation={props.conversation}
          hasValidationErrors={props.hasValidationErrors}
          areSendShortcutsDisabled={props.areSendShortcutsDisabled}
          hasClosePermission={props.hasClosePermission}
          hasSendPermission={props.hasSendPermission}
          defaultSendAction={props.defaultSendAction}
          sendMessage={props.sendMessage}
          hasTrialExpired={props.hasTrialExpired}
          hasTeamTrialExpired={props.hasTeamTrialExpired}
          timelineVariation={props.timelineVariation}
          hasArchivePermission={archivePermissionResult.isAllowed}
        />
      )}
    </PermissionCheck>
  );
}

function maybeRenderKnowledgeBaseButton(props: FullComposerToolbarProps) {
  if (props.isUserVisitor) {
    return null;
  }

  if (props.isMarkdownEditor) {
    return null;
  }

  return <KnowledgeBaseToolbarButton {...props} />;
}
